import "../styles/Landing.css"

import arrow from "../assets/arrow.svg"
import chevron from "../assets/chevron.svg"
import shieldHipaa from "../assets/shield-hipaa.svg"
import shieldGdpr from "../assets/gdpr-compliant.svg"
import shieldCcpa from "../assets/ccpa_compliant.svg"
import encryptedFiles from "../assets/encrypted-files.svg"
import vaultEncryption from "../assets/vault-encryption.svg"
import audit from "../assets/audit.svg"
import myStoryPhoto from "../assets/my-story-waterfall.jpeg"
import dadTestimonialPhoto from "../assets/testimonial-mark-s.jpeg"
import stepTesttube from "../assets/step-testtube.jpg"
import stepProtocol from "../assets/step-protocol.png"
import stepProgress from "../assets/step-progress.png"

import { useEffect, useState, useRef } from 'react';
import { useNavigate, useOutletContext, Link } from "react-router-dom"
import { useScreen } from "../context/ScreenProvider"

import Header from "../components/Header.jsx"
import BottomSheet from "../components/BottomSheet/BottomSheet"
import useTestimonials from "../hooks/useTestimonials"
import { usingStaticFallback } from "../components/BloodHelixHero"

function Landing () {
    const { isMobile } = useScreen()
    const { scrollProgressRef, paused } = useOutletContext()
    const [scrolled, setScrolled] = useState(window.scrollY != 0)

    const [slideEntry, setSlideEntry] = useState(0)
    const [faq, setFaq] = useState(new Set())
    const [modal, setModal] = useState(null)

    const [reviewEntry, setReviewEntry] = useState(0)
    const reviewIntervalMs = 10000;
    const intervalRef = useRef(null)

    const nav = useNavigate()

    const modalContent = {
        security: {
            title: "Our Security Commitment",
            body: "Your data, your choice. PrimeMaxing ensures every layer is fully encrypted so your health data is unreadable to anyone but you and your doctor. We promise to never sell your data in any way. You have complete control: download your data anytime, or permanently delete it. We undergo independent security audits to verify we are compliant with:",
            list: [
                "HIPAA – Patient Data Protection",
                "GDPR – EU Privacy Laws",
                "CCPA – California Privacy Act"
            ]
        }
    }

    const securityBadges = [
        {
            icon: shieldHipaa,
            label: "HIPAA Compliant",
            info: "We meet U.S. federal standards for protecting your medical records and health information."
        },
        {
            icon: shieldGdpr,
            label: "GDPR Compliant",
            info: "We follow EU privacy law — you can access, export, or permanently erase your data anytime."
        },
        {
            icon: shieldCcpa,
            label: "CCPA Compliant",
            info: "We honor California privacy rights. We never sell your personal information."
        },
        {
            icon: encryptedFiles,
            label: "Secure Transfer",
            info: "Your files travel over encrypted connections, so they're unreadable while in transit."
        },
        {
            icon: vaultEncryption,
            label: "Encrypted Storage",
            info: "Your data is encrypted at rest — even in storage, it stays unreadable to anyone but you."
        },
        {
            icon: audit,
            label: "Independently Audited",
            info: "Third-party security experts audit us to verify our compliance claims."
        }
    ]

    const reviews = [
        {
            quote: "After 3 months on my protocol, my energy levels are completely different. I'm not crashing at 2pm anymore and my recovery from workouts cut in half.",
            author: "Coming Soon",
            caption: "Beta User — Coming Soon",
            photo: null
        },
        {
            quote: "I was skeptical at first, but seeing my biomarkers improve across the board after following the personalized recommendations was eye-opening.",
            author: "Coming Soon",
            caption: "Beta User — Coming Soon",
            photo: null
        },
        {
            quote: "I've been on my protocol for years now. But being able to use GPT to calibrate without worrying about data privacy is paramount for me.",
            caption: "Mark S (Dad) age: 66",
            photo: dadTestimonialPhoto
        },
        {
            quote: "Finally something that connects the dots. My doctor said everything was 'normal' but I knew something was off. This found it.",
            author: "Coming Soon",
            caption: "Beta User — Coming Soon",
            photo: null
        }
    ]

    const textReviews = useTestimonials()

    const UpdateSet = (i) => {
        if (faq.has(i)) {
            setFaq(prev => {
                const newSet = new Set(prev)
                newSet.delete(i)
                return newSet
            })
        } else {
            setFaq(prev => {
                const newSet = new Set(prev)
                newSet.add(i)
                return newSet
            })
        }
    }

    const StartInterval = () => {
        intervalRef.current = setInterval(() => {
            setReviewEntry(prev => (prev + 1) % reviews.length)
        }, reviewIntervalMs)
    }

    useEffect(() => {
        StartInterval()

        const scrollHandler = () => {
            setScrolled(window.scrollY != 0) 
        }

        window.addEventListener('scroll', scrollHandler)

        const escHandler = (e) => {
            if (e.key === 'Escape') setModal(null)
        }
        window.addEventListener('keydown', escHandler)

        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.classList.add("animate")
                    observer.unobserve(entry.target);
                }
            })
        })

        document.querySelectorAll(".bullet, .step").forEach(ele => {
            const elementTop = ele.getBoundingClientRect().top + window.scrollY
            if (window.scrollY > elementTop) {
                ele.classList.add("animate")
            } else {
                observer.observe(ele)
            }
        })

        return () => {
            window.removeEventListener('scroll', scrollHandler)
            window.removeEventListener('keydown', escHandler)
            clearInterval(intervalRef.current)
        }
    }, [])

    // Wheel-driven camera parallax on the helix: engage only while the hero
    // overlay is still in view (page scrolled to the top). Touch scrolls pass
    // through to normal page scrolling. When paused, let wheel events scroll
    // the page normally — consuming them would swallow the first N scrolls.
    useEffect(() => {
        if (paused) return
        // No 3D scene → no camera to drive. Hijacking the wheel here would only
        // swallow scrolls and make the scrollbar jitter, so let the page scroll.
        if (usingStaticFallback()) return
        const PARALLAX_PX = window.innerHeight * 0.8
        const onWheel = (e) => {
            const progress = scrollProgressRef.current
            const delta = e.deltaY
            if (delta > 0 && progress < 1 && window.scrollY <= 0) {
                e.preventDefault()
                scrollProgressRef.current = Math.min(1, progress + delta / PARALLAX_PX)
            } else if (delta < 0 && progress > 0 && window.scrollY <= 0) {
                e.preventDefault()
                scrollProgressRef.current = Math.max(0, progress + delta / PARALLAX_PX)
            }
        }
        window.addEventListener('wheel', onWheel, { passive: false })
        return () => window.removeEventListener('wheel', onWheel)
    }, [paused, scrollProgressRef])

    return (
        <div className="landing">
            {modal && (
                <BottomSheet onClose={() => setModal(null)} className="modal-panel">
                    {({ onClose }) => (
                        <>
                            <button className="modal-close" onClick={onClose}>&times;</button>
                            <h3>{modalContent[modal]?.title}</h3>
                            <p>{modalContent[modal]?.body}</p>
                            {modalContent[modal]?.list && (
                                <ul className="modal-list">
                                    {modalContent[modal].list.map((item, i) => (
                                        <li key={i}>{item}</li>
                                    ))}
                                </ul>
                            )}
                        </>
                    )}
                </BottomSheet>
            )}
            <Header />
            <section className="hero-overlay">
                <h1>Maximize your Prime</h1>
                <p>
                    Find the patterns your labs miss.
                    <br className="hero-subtitle-break" />
                    Turn them into your protocol.
                </p>
                <button
                    type="button"
                    className={`decode-blood-flash ${paused ? 'paused' : ''}`}
                    onClick={() => nav("/signup")}
                >
                    Decode your blood
                </button>
                <div className="hero-disclaimers">
                    <span>HIPAA Compliant</span>
                    <span>No Credit Card</span>
                    <span>Instant Results</span>
                </div>
            </section>
            <section className="how-it-works" id="howitworks">
                <h2>How It Works</h2>
                <p className="how-subtitle">Three steps</p>
                <button
                    type="button"
                    className="demo-cta-button"
                    onClick={() => nav("/demo")}
                >
                    Live Demo
                </button>
                <div className="steps">
                    <div className="step">
                        <img className="step-img" src={stepTesttube} alt="Blood test tube held by a gloved hand" />
                        <h3>Get Your Labs</h3>
                        <p>Order labs through us or upload existing labs. Tell us your goals. We tell you what tests you need.</p>
                    </div>
                    <div className="step">
                        <img className="step-img" src={stepProtocol} alt="Personalized supplement protocol generated from blood work" />
                        <h3>Get Your Protocol</h3>
                        <p>Our algorithms analyze your biomarker patterns and generate a prioritized plan.</p>
                    </div>
                    <div className="step">
                        <img className="step-img" src={stepProgress} alt="Biomarker chart improving from yellow to green over time" />
                        <h3>Follow &amp; Retest</h3>
                        <p>Retest in 3 months. Watch your scores move from yellow to green — and feel the difference.</p>
                    </div>
                </div>
            </section>
            <div className="showcase">
                <section className="story">
                    <div className="left">
                        <h3>This gave me my life back</h3>
                        <p>Seven years in bio research taught me what labs measure. Getting sick taught me what they miss.</p>
                        <p>I was in bed more than 2 years. I couldn't train, couldn't work, I lost my identity. Standard labs said I was "Fine." I wasn't.</p>
                        <p>What brought me back wasn’t "medical averages". It was a personalized protocol targeting patterns most doctors don't look for.</p>
                        <p>Today I'm 33, back in my prime. My dad is too — at 66 he trains, hikes, and explores with me. Personalized medicine isn't just about getting healthy. It’s about extending your prime. Not by years, but by decades.</p>
                        <p>I built PrimeMaxing so you don’t need what I had to get here: years in a lab, access to specialists, or knowing which signals actually matter.</p>
                        <p>This shouldn't be a privilege.</p>
                        <p className="signature">— Clay</p>
                    </div>
                    <div className="right">
                        <figure className="story-photo">
                            <img src={myStoryPhoto} alt="Clay with his father at a waterfall" />
                            <figcaption>Dad, 66, and me, 33 — Waterfall hike</figcaption>
                        </figure>
                    </div>
                </section>
                <div className="first">
                    <h2>Real People. Real Results.</h2>
                    <div className="reviews">
                        <div
                            className="left polaroid-stack"
                            onClick={() => {
                                clearInterval(intervalRef.current)
                                StartInterval()
                                setReviewEntry(prev => (prev + 1) % reviews.length)
                            }}
                        >
                            {reviews.map((review, i) => {
                                const offset = (i - reviewEntry + reviews.length) % reviews.length
                                return (
                                    <figure
                                        key={i}
                                        className={`polaroid polaroid-offset-${offset} ${offset === 0 ? "active" : ""}`}
                                        aria-hidden={offset !== 0}
                                    >
                                        <div className="polaroid-photo">
                                            {review.photo
                                                ? <img src={review.photo} alt={review.author} />
                                                : <span className="photo-placeholder" />}
                                        </div>
                                        <figcaption>{review.caption}</figcaption>
                                    </figure>
                                )
                            })}
                        </div>
                        <div className="right">
                            <p className="quote">
                                {reviews[reviewEntry].quote}
                            </p>
                            <h4>{reviews[reviewEntry].author}</h4>
                            <div className="slider">
                                {reviews.map((review, i) => {
                                    return (
                                    <div key={i} className={`slide ${reviewEntry == i ? "active" : ""}`} onClick={() => {
                                        clearInterval(intervalRef.current)
                                        StartInterval()
                                        setReviewEntry(i)
                                    }}></div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                    <div className="text-reviews-ticker">
                        <div className="ticker-track">
                            {[...textReviews, ...textReviews].map((r, i) => (
                                <div key={i} className="ticker-card">
                                    <p>"{r.quote}"</p>
                                    <span>— {r.author}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
                <div className="second">
                    <div className="left">
                        <h3>Getting Old Is Not A Part Of Life.</h3>
                        <p>Standard labs just catch disease. They don't tell you why you're tired at 2pm, or why your recovery takes forever. We use deep biomarker pattern detection to catch things before they're a "disease".</p>
                        <button onClick={() => {nav("/demo")}}>See How It Works<img src={arrow} /></button>
                    </div>
                    <div className="right">
                        <div className="ph"></div>
                    </div>
                </div>
                <div className="third">
                    <div className="left">
                        <div className="ph"></div>
                    </div>
                    <div className="right">
                        <h3>A Plan That's Actually Built for You.</h3>
                        <p>You get a prioritized list that tells you: what to take; how much to take; when to take it; why you need it; what it will do for you.</p>
                        <button onClick={() => {nav("/demo")}}>See a Sample Protocol<img src={arrow} /></button>
                    </div>
                </div>
                <div className="banner" id="security">
                    <h3>Your Data Stays Yours. Period.</h3>
                    <p>Bank-level encryption. Independently verified HIPAA compliance. We never sell, trade, or share your information with anyone except your chosen doctor. Ever. </p>
                    <div className="security-badges">
                        {securityBadges.map((badge, i) => (
                            <div className="badge" key={i} tabIndex={0}>
                                <img src={badge.icon} alt={badge.label} />
                                <span>{badge.label}</span>
                                <div className="badge-tooltip" role="tooltip">{badge.info}</div>
                            </div>
                        ))}
                    </div>
                    <button onClick={() => setModal('security')}>Learn About Our Security</button>
                </div>
                <div className="fourth" id="howitworks">
                    <h3>How We Help You<br/>Stay in Your Prime</h3>
                    <div className="content">
                        <div className="left">
                            <div className={`entry ${slideEntry == 0 ? "selected" : ""}`} onClick={() => {setSlideEntry(0)}}>
                                <h4>Patterns, Not Just Numbers</h4>
                                <p>Your markers don't exist in isolation. We connect the dots—inflammation, hormones, nutrients, metabolism. The full picture, not just pieces.</p>
                            </div>
                            <div className={`entry ${slideEntry == 1 ? "selected" : ""}`} onClick={() => {setSlideEntry(1)}}>
                                <h4>Supplements That Make Sense</h4>
                                <p>You'll know exactly what to take and why. Doses based on YOUR blood. Not a promoted sponsor.</p>
                            </div>
                            <div className={`entry ${slideEntry == 2 ? "selected" : ""}`} onClick={() => {setSlideEntry(2)}}>
                                <h4>Beyond Pills</h4>
                                <p>Sleep. Stress. Movement. Diet. You get the lifestyle tweaks that actually move the needle—based on what your body is telling us.</p>
                            </div>
                            <div className={`entry ${slideEntry == 3 ? "selected" : ""}`} onClick={() => {setSlideEntry(3)}}>
                                <h4>Track Your Progress</h4>
                                <p>Retest. See what's working. Adjust. Your protocol evolves as you do.</p>
                            </div>
                        </div>
                        <div className="right">
                            <div className="ph">
                                
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="email-capture">
                <h3>Not ready yet? Get our free guide.</h3>
                <p>Learn the sneaky signs of gluten intolerance everyone faces!</p>
                <div className="email-form">
                    <input type="email" placeholder="Enter your email" />
                    <button>Get the Free Guide</button>
                </div>
                <p className="privacy-note">No spam. Unsubscribe anytime.</p>
            </div>
            <div className="faq" id="faq">
                <h3>Got Questions? We've Got Answers.</h3>
                <p>Everything you need to know before getting started.</p>
                <div className="questions">
                    <div className={`entry ${faq.has(0) ? "open" : ""}`} onClick={() => {UpdateSet(0)}}>
                        <div className="top">
                            How is this different from what my doctor does?
                            <img src={chevron} />
                        </div>
                        <div className="bottom">
                            <p>Your doctor looks for disease. We look for optimization. Most 'normal' results have massive room for improvement. We find it.</p>
                        </div>
                    </div>
                    <div className={`entry ${faq.has(1) ? "open" : ""}`} onClick={() => {UpdateSet(1)}}>
                        <div className="top">
                            What labs do I need?
                            <img src={chevron} />
                        </div>
                        <div className="bottom">
                            <p>Upload any tests you already have. We tell you exactly what to order if you need more.</p>
                        </div>
                    </div>
                    <div className={`entry ${faq.has(2) ? "open" : ""}`} onClick={() => {UpdateSet(2)}}>
                        <div className="top">
                            Is this medical advice?
                            <img src={chevron} />
                        </div>
                        <div className="bottom">
                            <p>No. We don't diagnose or treat. We optimize. Always work with your doctor for medical concerns.</p>
                        </div>
                    </div>
                    <div className={`entry ${faq.has(3) ? "open" : ""}`} onClick={() => {UpdateSet(3)}}>
                        <div className="top">
                            Why no TRT or peptides?
                            <img src={chevron} />
                        </div>
                        <div className="bottom">
                            <p>We focus on what your body can do naturally. Diet. Lifestyle. Supplements. Using shortcuts burn out fast.</p>
                        </div>
                    </div>
                </div>
            </div>
            <div className="footer">
                <div className="info">
                    <div className="top">
                        <div className="left">
                            <h4>PrimeMaxing</h4>
                            <p>Stay in your prime forever.</p>
                        </div>
                        <div className="col">
                            <h4>Product</h4>
                            <p><a href="#howitworks">How It Works</a></p>
                            <p><Link to="/pricing">Pricing</Link></p>
                        </div>
                        <div className="col">
                            <h4>Company</h4>
                            <p><a href="#about">About Us</a></p>
                            <p><a href="#science">The Science</a></p>
                        </div>
                        <div className="col">
                            <h4>Trust & Security</h4>
                            <p><a href="#security">Data Security</a></p>
                            <p><a href="#faq">FAQ</a></p>
                            <p><Link to="/legal/data-protection">Privacy Policy</Link></p>
                            <p><Link to="/legal/service-agreement">Terms of Service</Link></p>
                            <p><Link to="/legal/refund-policy">Refund Policy</Link></p>
                        </div>
                    </div>
                    <div className="bottom">
                        <p className="copyright">
                            © 2026 PrimeMaxing. All rights reserved.
                        </p>
                        <Link to="/legal/data-protection" className="link">
                            Privacy Policy
                        </Link>
                        <Link to="/legal/service-agreement" className="link">
                            Terms of Service
                        </Link>
                        <Link to="/legal/refund-policy" className="link">
                            Refund Policy
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Landing;